import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { log } from "../../lib/log";

const journeyPayload = (plan) => ({
  destination: plan.destination, why: plan.why, itinerary: plan.itinerary, budget: plan.budget, inputs: plan.inputs,
});

// Save-to-library + "Send to Lunamore" concierge forwarding for a generated plan.
export function useJourneyActions(plan) {
  const [savedJourneyId, setSavedJourneyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const forwardingRef = useRef(false);

  useEffect(() => {
    setSavedJourneyId(null);
    setSaving(false);
    forwardingRef.current = false;
  }, [plan]);

  const persist = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/journeys", journeyPayload(plan));
      setSavedJourneyId(data.journey_id);
      return data.journey_id;
    } finally {
      setSaving(false);
    }
  };

  const saveJourney = async () => {
    if (!plan || savedJourneyId) return;
    try {
      await persist();
      toast.success("Journey saved to your library");
    } catch (e) {
      log.error("Save journey failed:", e);
      toast.error("Could not save journey. Try again.");
    }
  };

  // Fired on mousedown + click; guard against double execution racing with tab navigation.
  const forwardToLunamore = async () => {
    if (forwardingRef.current) return;
    forwardingRef.current = true;
    let jid = savedJourneyId;
    if (!jid) {
      try { jid = await persist(); } catch (e) { log.error("Auto-save before Lunamore forward failed:", e); }
    }
    if (!jid) return;
    try {
      await api.post(`/journeys/${jid}/forward`);
      toast.success("Sent to Lunamore concierge");
    } catch (e) {
      log.warn("Lunamore forward request failed:", e);
    }
  };

  return { savedJourneyId, saving, saveJourney, forwardToLunamore };
}

// Hero image URL + compatible travelers for the plan's destination.
export function usePlanExtras(plan) {
  const [heroUrl, setHeroUrl] = useState(null);
  const [companions, setCompanions] = useState([]);
  useEffect(() => {
    setHeroUrl(null);
    setCompanions([]);
    const city = plan?.destination?.city;
    if (!city) return;
    let alive = true;
    setHeroUrl(`${API}/places/image?city=${encodeURIComponent(city)}&planet=scenery&w=960`);
    api.get(`/journeys/compatible/travelers?destination=${encodeURIComponent(city)}&timing=${encodeURIComponent(plan.inputs?.timing || "")}`)
      .then((r) => { if (alive) setCompanions(r.data || []); })
      .catch((e) => log.debug("compatible-travelers fetch failed:", e?.response?.status || e?.message));
    return () => { alive = false; };
  }, [plan]);
  return { heroUrl, companions };
}
